import React, { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";

/**
 * HealingModal Component
 * 
 * A reusable modal for displaying and approving test case healing proposals.
 * Shows proposed selector fixes for failed test case steps with confidence scores.
 * 
 * Props:
 * - isOpen: Boolean to control modal visibility
 * - onClose: Callback function when modal is closed
 * - testCaseId: ID of the test case to heal
 * - testCaseName: Name of the test case (for display)
 * - onHealingComplete: Callback after successful healing approval
 */
const HealingModal = ({
  isOpen,
  onClose,
  testCaseId,
  testCaseName,
  onHealingComplete,
}) => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProposals, setSelectedProposals] = useState({});

  const fetchHealingProposals = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `/self_healing/get_healing_proposal?test_case_id=${testCaseId}`
      );
      setProposals(response?.proposals || []);


      const initialSelection = {};
      (response?.proposals || []).forEach((proposal) => {
        initialSelection[proposal.step_id] = true;
      });
      setSelectedProposals(initialSelection);
    } catch (err) {
      console.error("Error fetching healing proposals:", err);
      setError("Failed to fetch healing proposals. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveHealing = async () => {
    setApproving(true);
    try {
      // Build approvals array from selected proposals
      const approvals = proposals
        .filter((proposal) => selectedProposals[proposal.step_id])
        .map((proposal) => ({
          step_id: proposal.step_id,
          new_selector: proposal.proposed_selector,
          step_index: proposal.step_index,
        }));

      const payload = {
        test_case_id: testCaseId,
        approvals,
      };
  
      const response = await axiosInstance.post(
        "/self_healing/approve_healing",
        payload
      );
      if (response?.status_code == "200" || response.status === 200) {
        onHealingComplete?.();
        onClose();
      }
    } catch (err) {
      console.error("Error approving healing:", err);
      setError("Failed to approve healing. Please try again.");
    } finally {
      setApproving(false);
    }
  };

  /**
   * Handle proposal selection toggle
   */
  const toggleProposalSelection = (stepId) => {
    setSelectedProposals((prev) => ({
      ...prev,
      [stepId]: !prev[stepId],
    }));
  };

  /**
   * Get confidence score badge color
   */
  const getConfidenceColor = (score) => {
    if (score >= 90) return "bg-green-100 text-green-800";
    if (score >= 75) return "bg-blue-100 text-blue-800";
    return "bg-yellow-100 text-yellow-800";
  };

  // Fetch proposals when modal opens
  useEffect(() => {
    if (isOpen && testCaseId) {
      fetchHealingProposals();
    }
  }, [isOpen, testCaseId]);

  if (!isOpen) return null;

  return (
    <div data-rt-modal-overlay className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001]" onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              🔧 Heal Test Case Script
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {testCaseName} - Review and approve selector fixes
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none"
            disabled={approving}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-gray-600">
                Loading healing proposals...
              </span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
              <button
                onClick={fetchHealingProposals}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Retry
              </button>
            </div>
          ) : proposals.length > 0 ? (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                <span className="text-blue-600 text-lg mt-0.5">ℹ️</span>
                <p className="text-sm text-blue-800">
                  Review the proposed selector changes below. Selectors with
                  higher confidence scores are more likely to be correct.
                </p>
              </div>

              {/* Proposals Table */}
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {/* <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={
                            proposals.length > 0 &&
                            proposals.every((p) => selectedProposals[p.step_id])
                          }
                          onChange={() => {
                            const allSelected = proposals.every(
                              (p) => selectedProposals[p.step_id]
                            );
                            const newSelection = {};
                            proposals.forEach((p) => {
                              newSelection[p.step_id] = !allSelected;
                            });
                            setSelectedProposals(newSelection);
                          }}
                          className="w-4 h-4 cursor-pointer"
                          title="Select/Deselect all"
                        />
                      </th> */}
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                        Step Description
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                        Original Selector
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                        Proposed Selector
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                        Confidence
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposals.map((proposal, index) => (
                      <tr
                        key={proposal.step_id}
                        className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                          index === proposals.length - 1 ? "border-b-0" : ""
                        }`}
                      >
                        {/* <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedProposals[proposal.step_id] || false}
                            onChange={() =>
                              toggleProposalSelection(proposal.step_id)
                            }
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td> */}
                        <td className="px-4 py-3">
                          <div className="max-w-xs">
                            <p className="text-sm font-medium text-gray-900">
                              {proposal.description}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Step {proposal.step_index + 1}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="max-w-xs">
                            <code className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded block break-words">
                              {proposal.original_selector}
                            </code>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="max-w-xs">
                            <code className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded block break-words border border-green-200">
                              {proposal.proposed_selector}
                            </code>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getConfidenceColor(
                              proposal.confidence_score
                            )}`}
                          >
                            {proposal.confidence_score}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Info Message */}
              {/* <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <span className="text-amber-600 text-lg mt-0.5">⚠️</span>
                <p className="text-sm text-amber-800">
                  {Object.values(selectedProposals).filter(Boolean).length} of{" "}
                  {proposals.length} proposals selected for approval.
                </p>
              </div> */}
            </div>
          ) : (
            <div className="flex justify-center items-center py-12">
              <p className="text-gray-500">
                No healing proposals available for this test case.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={approving}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApproveHealing}
            disabled={
              approving ||
              loading ||
              Object.values(selectedProposals).every((v) => !v)
            }
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {approving && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {approving ? "Healing..." : " Approve and Heal"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HealingModal;
